import React, {
  useCallback, useMemo, useRef, useState,
} from 'react';

import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { Button, Icon } from '@picsio/ui';
import { Info } from '@picsio/icons';

import localization from '../../../../shared/strings';
import DialogIcon from '../../../Icon';
import Tooltip from '../../../Tooltip';
import ChannelTabsHeader from '../MediaSelection/ChannelTabsHeader';
import VariantMediaSection from '../MediaSelection/VariantMediaSection';
import { countAssignedVariantsForChannel } from '../helpers/syncFlowMediaHelpers';
import {
  assignAssetToVariant,
  getDefaultExpandedVariantIds,
  groupAssetsByVariant,
  toggleExpandedVariantIds,
  unassignAssetFromVariant,
} from '../helpers/variantMediaAssignmentHelpers';
import { normalizeVariantId } from '../helpers/toggleVariantAssignment';
import ButtonLoader from '../ButtonLoader';
import styles from '../styles.module.scss';

function VariantMediaAssignmentDialog({
  channelOptions,
  selectedChannels,
  variantOptions,
  variantMediaByChannel,
  validateBeforeFinish,
  onConfirm,
  onBack,
  onCancel,
  onValidationAbort,
  root,
}) {
  const strings = localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL;
  const mediaStrings = strings.VARIANT_MEDIA;

  const channels = useMemo(
    () => channelOptions.filter(({ value }) => selectedChannels.includes(value)),
    [channelOptions, selectedChannels],
  );

  const [activeChannelId, setActiveChannelId] = useState(channels[0]?.value);
  const [assignmentsByChannel, setAssignmentsByChannel] = useState(() => (
    selectedChannels.reduce((acc, channelId) => ({
      ...acc,
      [channelId]: { ...(variantMediaByChannel[channelId] || {}) },
    }), {})
  ));
  const [expandedVariantIds, setExpandedVariantIds] = useState(
    () => getDefaultExpandedVariantIds(variantOptions),
  );
  const [isConfirming, setIsConfirming] = useState(false);

  const activeChannel = channels.find(({ value }) => value === activeChannelId) || channels[0];
  const channelAssignments = assignmentsByChannel[activeChannel?.value] || {};
  const isMultiChannel = channels.length > 1;
  const totalVariantCount = variantOptions.length;

  const assignedCount = countAssignedVariantsForChannel({
    variantMediaByChannel: assignmentsByChannel,
    channelId: activeChannel?.value,
    totalVariantCount,
  });

  const assetsByVariant = useMemo(
    () => groupAssetsByVariant(
      channelAssignments,
      variantOptions.map(({ id }) => id),
    ),
    [channelAssignments, variantOptions],
  );

  const isDestroyedRef = useRef(false);

  const destroy = useCallback(() => {
    if (isDestroyedRef.current) {
      return;
    }

    isDestroyedRef.current = true;

    try {
      root.unmount();
    } catch { /* ignore */ }
  }, [root]);

  const handleCancel = useCallback(() => {
    destroy();
    onCancel?.();
  }, [destroy, onCancel]);

  const handlePrevious = useCallback(() => {
    destroy();
    onBack?.(assignmentsByChannel);
  }, [assignmentsByChannel, destroy, onBack]);

  const handleChannelChange = useCallback((channelId) => {
    setActiveChannelId(channelId);
    setExpandedVariantIds(getDefaultExpandedVariantIds(variantOptions));
  }, [variantOptions]);

  const updateChannelAssignments = useCallback((nextAssignments) => {
    setAssignmentsByChannel((prev) => ({
      ...prev,
      [activeChannel.value]: nextAssignments,
    }));
  }, [activeChannel.value]);

  const handleToggleExpand = useCallback((variantId) => {
    setExpandedVariantIds((prev) => toggleExpandedVariantIds(prev, variantId));
  }, []);

  const handleSelectAsset = useCallback((variantId, assetId) => {
    const normalizedVariantId = normalizeVariantId(variantId);
    const normalizedAssetId = String(assetId);
    const assignedAssetIds = assetsByVariant[normalizedVariantId] || [];
    const isAlreadyAssigned = assignedAssetIds.includes(normalizedAssetId);

    if (isAlreadyAssigned) {
      updateChannelAssignments(unassignAssetFromVariant({
        assignments: channelAssignments,
        assetId: normalizedAssetId,
        variantId: normalizedVariantId,
      }));
      return;
    }

    updateChannelAssignments(assignAssetToVariant({
      assignments: channelAssignments,
      assetId: normalizedAssetId,
      variantId: normalizedVariantId,
    }));
  }, [
    assetsByVariant,
    channelAssignments,
    updateChannelAssignments,
  ]);

  const handleResetAll = useCallback(() => {
    setAssignmentsByChannel((prev) => ({
      ...prev,
      [activeChannel.value]: {},
    }));
  }, [activeChannel.value]);

  const handleConfirm = useCallback(async () => {
    if (validateBeforeFinish) {
      setIsConfirming(true);

      try {
        await validateBeforeFinish(() => {
          setIsConfirming(false);
          destroy();
        });
        destroy();
        onConfirm(assignmentsByChannel);
      } catch {
        if (isDestroyedRef.current) {
          onValidationAbort?.();
          return;
        }

        setIsConfirming(false);
      }

      return;
    }

    destroy();
    onConfirm(assignmentsByChannel);
  }, [
    assignmentsByChannel,
    destroy,
    onConfirm,
    validateBeforeFinish,
    onValidationAbort,
  ]);

  const getBadgeCount = useCallback(
    (channelId) => countAssignedVariantsForChannel({
      variantMediaByChannel: assignmentsByChannel,
      channelId,
      totalVariantCount,
    }),
    [assignmentsByChannel, totalVariantCount],
  );

  const counterText = mediaStrings.COUNTER(assignedCount, totalVariantCount);

  const channelLabel = activeChannel?.label || '';

  const title = isMultiChannel
    ? mediaStrings.TITLE_MULTI
    : mediaStrings.TITLE_SINGLE(channelLabel);

  const description = isMultiChannel
    ? mediaStrings.DESCRIPTION_MULTI
    : mediaStrings.DESCRIPTION_SINGLE;

  // TODO (future): Handle unsupported media format for single-channel direct attach.

  return (
    <div className={`simpleDialog ${styles.connectChannelSectionModal} ${styles.connectChannelSectionModalWide} ${styles.connectChannelSectionDialogWithSubheader}`}>
      <div className="simpleDialogUnderlayer" />
      <div className={`simpleDialogBox ${styles.connectChannelSectionModalBoxWide}`}>
        <div className="simpleDialogHeader">
          <span className="simpleDialogTitle">{title}</span>
          <span
            role="button"
            tabIndex={0}
            className="simpleDialogBtnCross"
            onClick={handleCancel}
            onKeyDown={handleCancel}
          >
            <DialogIcon name="close" />
          </span>
        </div>
        <div className="simpleDialogSubHeader">
          <p>{description}</p>
        </div>
        <div className="simpleDialogContent">
          <div className="simpleDialogContentInner">
            <If condition={isMultiChannel}>
              <ChannelTabsHeader
                channels={channels}
                activeChannelId={activeChannel.value}
                getBadgeCount={getBadgeCount}
                onChange={handleChannelChange}
              />
            </If>
            <div className={styles.connectChannelSectionVariantToolbar}>
              <Tooltip content={mediaStrings.COUNTER_TOOLTIP} placement="top">
                <span className={styles.connectChannelSectionVariantCounterInfo}>
                  <Icon size="lg">
                    <Info />
                  </Icon>
                </span>
              </Tooltip>
              <span className={styles.connectChannelSectionVariantCounter}>{counterText}</span>
              <button
                type="button"
                className={styles.connectChannelSectionVariantReset}
                onClick={handleResetAll}
              >
                {mediaStrings.RESET_ALL}
              </button>
            </div>
            <div className={styles.connectChannelSectionVariantList}>
              {variantOptions.map(({ id, label, attachedAssets }) => {
                const normalizedVariantId = normalizeVariantId(id);
                const selectedAssetIds = assetsByVariant[normalizedVariantId] || [];

                return (
                  <VariantMediaSection
                    key={id}
                    variantLabel={label}
                    poolAssets={attachedAssets}
                    selectedAssetIds={selectedAssetIds}
                    isExpanded={expandedVariantIds.has(normalizedVariantId)}
                    onToggleExpand={() => handleToggleExpand(id)}
                    onSelectAsset={(assetId) => handleSelectAsset(id, assetId)}
                  />
                );
              })}
            </div>
          </div>
        </div>
        <div className="simpleDialogFooter">
          <Button
            color="secondary"
            size="md"
            variant="contained"
            disabled={isConfirming}
            onClick={handleCancel}
          >
            {strings.BTN_CANCEL}
          </Button>
          <If condition={onBack}>
            <Button
              color="secondary"
              size="md"
              variant="contained"
              disabled={isConfirming}
              onClick={handlePrevious}
            >
              {strings.BTN_PREVIOUS}
            </Button>
          </If>
          <Button
            color="primary"
            size="md"
            variant="contained"
            disabled={isConfirming}
            endIcon={isConfirming ? <ButtonLoader /> : null}
            onClick={handleConfirm}
          >
            {strings.BTN_CONFIRM_VARIANT_MEDIA}
          </Button>
        </div>
      </div>
    </div>
  );
}

VariantMediaAssignmentDialog.propTypes = {
  channelOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    type: PropTypes.string,
  })).isRequired,
  selectedChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
  variantOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    attachedAssets: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
    })).isRequired,
  })).isRequired,
  variantMediaByChannel: PropTypes.objectOf(PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))),
  validateBeforeFinish: PropTypes.func,
  root: PropTypes.shape({ unmount: PropTypes.func.isRequired }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onCancel: PropTypes.func,
  onValidationAbort: PropTypes.func,
};

VariantMediaAssignmentDialog.defaultProps = {
  variantMediaByChannel: {},
  validateBeforeFinish: undefined,
  onBack: undefined,
  onCancel: undefined,
  onValidationAbort: undefined,
};

export function showVariantMediaAssignmentDialog(props) {
  const $wrapper = document.querySelector('.wrapperDialog');
  const root = createRoot($wrapper);
  root.render(
    <VariantMediaAssignmentDialog
      {...props}
      root={root}
    />,
  );
}

export default VariantMediaAssignmentDialog;
